/* global NotificationOptions */
import { noteExpectedFailure } from '@utils/observability/expectedFailure';

// Chrome on Android forbids `new Notification()` by contract: the constructor throws
// `TypeError: Failed to construct 'Notification': Illegal constructor` synchronously, and the only
// supported way to display is `ServiceWorkerRegistration.showNotification()`. Sentry issue #125
// (7421810799) counted 6,112 events / 184 users since 2026-04-18 — every one of them `os:Android`
// or an Android webview — and each burned a session replay (`replaysOnErrorSampleRate: 1.0`).
// Worse than the noise: the throw happened BEFORE `notification.onclick` was assigned, so the whole
// notification, banner and routing included, was lost.
//
// The constructor is still tried FIRST, on purpose, and the reason is NOT Electron's service worker
// timing — the panel no longer registers a worker inside Electron at all
// (`@utils/pwa/serviceWorkerPolicy`). It is `orderDesktopNotify`: that path is fed by the websocket
// (`useIncomingOrder.tsx:288`) and therefore DOES run on the desktop shell, where `getRegistration()`
// resolves `undefined` and a service-worker-first helper would show nothing at all. The constructor
// works there. Going constructor-first can only ADD a path where the constructor is illegal.
//
// The click is handled in two different places because `showNotification()` has no `onclick`. On
// the fallback path the click surfaces as `notificationclick` inside `src/firebase-messaging-sw.js`,
// which focuses the tab and posts `NOTIFICATION_CLICK` with `event.notification.data` back to the
// page (consumed in `PushNotificationContext`). That is why `data` MUST travel inside `options`: it
// is the only channel the worker has to know where to route. Drop it and the click silently only
// focuses the tab.
export interface NativeNotificationInput {
  title: string;
  options?: NotificationOptions;
  // Constructor path only (desktop / Electron). On the service-worker fallback the click belongs to
  // the worker's `notificationclick`, so anything the click needs must be reachable from
  // `options.data`.
  onClick?: (notification: Notification, event: Event) => void;
}

export const showNativeNotification = async ({ title, options, onClick }: NativeNotificationInput): Promise<void> => {
  // Outer guard over the WHOLE body: callers fire this and forget it (`void showNativeNotification`),
  // so any rejection escaping here would surface as an `unhandledrejection` and mint a brand new
  // high-volume Sentry issue — exactly the noise this change exists to remove.
  try {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;

    try {
      const notification = new Notification(title, options);
      if (onClick) {
        notification.onclick = (event) => onClick(notification, event);
      }
      return;
    } catch {
      // Fall through on ANY constructor failure. Matching the Android message text would be brittle
      // across engines and the fallback below is itself guarded, so retrying costs nothing.
    }

    // Same guard the service-worker effect of `PushNotificationContext` already documents:
    // `navigator.serviceWorker` is undefined in private-mode Safari/Firefox and in some webviews.
    if (typeof navigator === 'undefined' || !navigator.serviceWorker) return;

    // `getRegistration()` and not `navigator.serviceWorker.ready`: `ready` never settles when no
    // worker was ever registered, and this runs once per incoming message — that would leak one
    // pending promise per order.
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) return;

    await registration.showNotification(title, options);
  } catch (error) {
    // A breadcrumb and not `captureException`: the native banner reinforces the in-app alarm and the
    // NotificationCenter card, both of which already fired, so the user is not broken. Replacing a
    // 6,112-event issue with a new one would defeat the purpose.
    noteExpectedFailure('pwa', 'native notification fallback failed', {
      error: error instanceof Error ? error.message : String(error),
    });
  }
};
